import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, MessageCircle } from "lucide-react";
import { ParallaxBackground } from "@/components/site/ParallaxBackground";
import { Header } from "@/components/site/Header";
import { FAQS, FAQ_AMATEUR, FAQ_SEMINAR, FAQ_REFUND, FAQ_ABOUT, FAQ_SEMINAR_ABOUT, REFUND_POLICY_INTRO, WHATSAPP_NUMBER } from "@/lib/data";

const CATEGORIES = [
  { id: "general", label: "Championship" },
  { id: "amateur", label: "Amateur Fight" },
  { id: "seminar", label: "Seminar" },
  { id: "refund", label: "Refund & Return" },
];

const ABOUTS = { general: FAQ_ABOUT, seminar: FAQ_SEMINAR_ABOUT, refund: REFUND_POLICY_INTRO };
const ABOUT_TITLES = { seminar: "About The Seminar", refund: "About The Refund Policy" };

export const AnswerText = ({ text }) => {
  const phrase = "Refund & Return Policy";
  const parts = text.split(phrase);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <Link to="/refund-policy" className="text-neon-cyan underline underline-offset-2 transition-colors hover:text-white">
              {phrase}
            </Link>
          )}
          {part}
        </React.Fragment>
      ))}
    </>
  );
};

export const FaqItem = ({ item, index, open, onToggle }) => (
  <div
    className={`overflow-hidden rounded-2xl border transition-colors ${open ? "border-neon-cyan/40 bg-surface/90" : "border-white/10 bg-surface/60"}`}
    data-testid={`faq-item-${index + 1}`}
  >
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      data-testid={`faq-q-${index + 1}`}
    >
      <span className="flex items-baseline gap-3">
        <span className={`font-display text-sm ${open ? "text-neon-cyan" : "text-neon-pink/60"}`}>{String(index + 1).padStart(2, "0")}</span>
        <span className="font-sub text-sm font-bold text-white sm:text-base">{item.q}</span>
      </span>
      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-neon-cyan" : "text-slate-500"}`} />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className="whitespace-pre-line px-5 pb-5 pl-12 font-body text-sm leading-relaxed text-slate-300" data-testid={`faq-a-${index + 1}`}>
            <AnswerText text={item.a} />
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function FaqPage() {
  const [openIdx, setOpenIdx] = useState(0);
  const [cat, setCat] = useState("general");
  const items = cat === "amateur" ? FAQ_AMATEUR : cat === "seminar" ? FAQ_SEMINAR : cat === "refund" ? FAQ_REFUND : FAQS;

  const switchCat = (id) => {
    setCat(id);
    setOpenIdx(0);
  };

  return (
    <div className="relative min-h-screen text-white">
      <ParallaxBackground />
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-8" data-testid="faq-page">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono-cap mb-3 text-[11px] text-neon-cyan">Wilayah Pahlawan 3.0 · MuaySport</p>
          <h1 className="font-display text-4xl leading-[0.95] sm:text-6xl">
            FREQUENTLY ASKED <span className="bg-gradient-to-r from-violet-400 via-cyan-300 to-green-400 bg-clip-text text-transparent">QUESTIONS</span>
          </h1>

          {ABOUTS[cat] && (
            <div className="mt-8 rounded-3xl border border-white/10 bg-surface/80 p-6 sm:p-7" data-testid="faq-about">
              <p className="font-mono-cap mb-2 flex items-center gap-2 text-[10px] text-neon-pink">
                <HelpCircle className="h-4 w-4" /> {ABOUT_TITLES[cat] || "About The Event"}
              </p>
              <p className="font-body text-sm leading-relaxed text-slate-300">{ABOUTS[cat]}</p>
            </div>
          )}
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="faq-tabs">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => switchCat(c.id)}
              className={`rounded-full px-5 py-2.5 font-mono-cap text-[10px] transition-colors ${
                cat === c.id
                  ? "bg-gradient-to-r from-neon-pink to-neon-violet text-white"
                  : "border border-white/15 text-slate-400 hover:border-neon-cyan hover:text-neon-cyan"
              }`}
              data-testid={`faq-tab-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {items.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              index={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>

        {cat === "refund" && (
          <div className="mt-6 text-center">
            <Link
              to="/refund-policy"
              className="inline-flex items-center gap-2 rounded-full border border-neon-cyan/40 px-6 py-3 font-mono-cap text-[11px] text-neon-cyan transition-colors hover:bg-neon-cyan/10"
              data-testid="faq-full-policy-link"
            >
              Read Full Policy — Refund &amp; Return Policy
            </Link>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-10 flex flex-col items-center gap-3 rounded-3xl border border-neon-pink/30 bg-neon-pink/5 p-6 text-center"
          data-testid="faq-support"
        >
          <p className="font-sub text-sm font-bold text-white">Still have questions?</p>
          <p className="font-body text-xs text-slate-400">Our support team is ready to help via WhatsApp.</p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            className="mt-1 flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-6 py-3 font-mono-cap text-[11px] text-white"
            data-testid="faq-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp Support
          </a>
        </motion.div>
      </main>
    </div>
  );
}
